// WorkflowAnalytics.js
// Full-page analytics dashboard for workflow & task metrics.

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Divider,
} from '@mui/material';
import {
  Assignment,
  CheckCircle,
  Warning,
  Schedule,
  TrendingUp,
  Person,
  Speed,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useTheme } from '@mui/material/styles';
import { enhancedApi } from '../api';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const fmtNum = (n) => (n != null ? Number(n).toLocaleString() : '—');

const fmtDays = (n) => {
  if (n == null) return '—';
  const days = parseFloat(n);
  return isNaN(days) ? '—' : `${days.toFixed(1)}d`;
};

// ---------------------------------------------------------------------------
// SummaryCard
// ---------------------------------------------------------------------------

const SummaryCard = ({ icon, label, value, colorKey, subtitle }) => {
  const theme = useTheme();
  const color = theme.palette[colorKey]?.main ?? theme.palette.primary.main;
  return (
    <Paper
      elevation={2}
      sx={{
        flex: '1 1 180px',
        minWidth: 150,
        p: 2,
        borderTop: (t) => `4px solid ${t.palette[colorKey]?.main ?? t.palette.primary.main}`,
        borderRadius: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Box sx={{ color: (t) => t.palette[colorKey]?.main ?? t.palette.primary.main }}>{icon}</Box>
        <Typography variant="body2" color="text.secondary" fontWeight={500}>
          {label}
        </Typography>
      </Box>
      <Typography
        variant="h4"
        fontWeight={800}
        sx={{ color: (t) => t.palette[colorKey]?.main ?? t.palette.primary.main }}
      >
        {value}
      </Typography>
      {subtitle && (
        <Typography variant="caption" color="text.disabled" display="block" mt={0.25}>
          {subtitle}
        </Typography>
      )}
    </Paper>
  );
};

// ---------------------------------------------------------------------------
// HeadCell
// ---------------------------------------------------------------------------

const HeadCell = ({ children, align = 'left' }) => (
  <TableCell
    align={align}
    sx={{
      fontWeight: 700,
      bgcolor: 'grey.100',
      color: 'text.secondary',
      fontSize: 13,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      py: 1.25,
      whiteSpace: 'nowrap',
    }}
  >
    {children}
  </TableCell>
);

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

const WorkflowAnalytics = () => {
  const theme = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await enhancedApi.getWorkflowAnalytics();
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load analytics.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const summary = data?.summary ?? {};
  const byTemplate = Array.isArray(data?.by_template) ? data.by_template : [];
  const weeklyTrend = Array.isArray(data?.weekly_trend) ? data.weekly_trend : [];
  const bottleneckTasks = Array.isArray(data?.bottleneck_tasks) ? data.bottleneck_tasks : [];
  const staffWorkload = Array.isArray(data?.staff_workload) ? data.staff_workload : [];

  const primaryColor = theme.palette.primary.main;
  const successColor = theme.palette.success.main;
  const errorColor = theme.palette.error.main;
  const secondaryColor = theme.palette.secondary.main;

  const renderSummary = () => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
      <SummaryCard
        icon={<Assignment />}
        label="Total Active"
        value={fmtNum(summary.total_active)}
        colorKey="primary"
        subtitle="Currently in-progress workflows"
      />
      <SummaryCard
        icon={<CheckCircle />}
        label="Completed This Month"
        value={fmtNum(summary.completed_this_month)}
        colorKey="success"
        subtitle="Workflows completed this month"
      />
      <SummaryCard
        icon={<Warning />}
        label="Overdue"
        value={fmtNum(summary.overdue)}
        colorKey="error"
        subtitle="Past their due date"
      />
      <SummaryCard
        icon={<Schedule />}
        label="On-Time Rate"
        value={summary.on_time_rate != null ? `${Number(summary.on_time_rate).toFixed(0)}%` : '—'}
        colorKey="secondary"
        subtitle="Workflows completed on time"
      />
    </Box>
  );

  const renderByTemplateChart = () => (
    <Paper elevation={1} sx={{ mb: 3, borderRadius: 2, overflow: 'hidden', p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <TrendingUp sx={{ color: 'primary.main', fontSize: 20 }} />
        <Typography variant="subtitle1" fontWeight={700}>
          Workflows by Template
        </Typography>
      </Box>
      <Divider sx={{ mb: 2 }} />
      {byTemplate.length === 0 ? (
        <Typography variant="body2" color="text.disabled" sx={{ py: 4, textAlign: 'center' }}>
          No template data available
        </Typography>
      ) : (
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={byTemplate} margin={{ top: 10, right: 20, left: 0, bottom: 60 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
            <XAxis dataKey="template_name" angle={-45} textAnchor="end" height={80} tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip />
            <Legend />
            <Bar dataKey="completed" fill={successColor} name="Completed" />
            <Bar dataKey="created" fill={primaryColor} name="Created" />
          </BarChart>
        </ResponsiveContainer>
      )}
    </Paper>
  );

  const renderWeeklyTrendChart = () => (
    <Paper elevation={1} sx={{ mb: 3, borderRadius: 2, overflow: 'hidden', p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <TrendingUp sx={{ color: 'primary.main', fontSize: 20 }} />
        <Typography variant="subtitle1" fontWeight={700}>
          Weekly Completion Trend
        </Typography>
      </Box>
      <Divider sx={{ mb: 2 }} />
      {weeklyTrend.length === 0 ? (
        <Typography variant="body2" color="text.disabled" sx={{ py: 4, textAlign: 'center' }}>
          No trend data available
        </Typography>
      ) : (
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={weeklyTrend} margin={{ top: 10, right: 20, left: 0, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
            <XAxis dataKey="week_label" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="completions" stroke={primaryColor} name="Completions" strokeWidth={2} dot={{ r: 4 }} />
          </LineChart>
        </ResponsiveContainer>
      )}
    </Paper>
  );

  const renderBottleneckTable = () => (
    <Paper elevation={1} sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Speed sx={{ color: 'warning.main', fontSize: 20 }} />
        <Typography variant="subtitle1" fontWeight={700}>
          Bottleneck Tasks
        </Typography>
      </Box>
      <Divider />
      <TableContainer sx={{ maxHeight: 360 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <HeadCell>Task Name</HeadCell>
              <HeadCell align="right">Avg Delay (days)</HeadCell>
              <HeadCell align="right">Count</HeadCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {bottleneckTasks.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 3, color: 'text.disabled' }}>
                  No bottleneck tasks detected
                </TableCell>
              </TableRow>
            ) : (
              bottleneckTasks.map((item, idx) => (
                <TableRow key={item.task_name ?? idx} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {item.task_name ?? item.name ?? '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">{fmtDays(item.avg_delay_days ?? item.avg_delay)}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">{fmtNum(item.count)}</Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );

  const renderStaffWorkloadChart = () => (
    <Paper elevation={1} sx={{ mb: 3, borderRadius: 2, overflow: 'hidden', p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Person sx={{ color: 'primary.main', fontSize: 20 }} />
        <Typography variant="subtitle1" fontWeight={700}>
          Staff Workload
        </Typography>
      </Box>
      <Divider sx={{ mb: 2 }} />
      {staffWorkload.length === 0 ? (
        <Typography variant="body2" color="text.disabled" sx={{ py: 4, textAlign: 'center' }}>
          No staff workload data available
        </Typography>
      ) : (
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={staffWorkload} margin={{ top: 10, right: 20, left: 0, bottom: 60 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
            <XAxis dataKey="user_name" angle={-45} textAnchor="end" height={80} tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip />
            <Legend />
            <Bar dataKey="assigned_count" fill={primaryColor} name="Assigned" />
            <Bar dataKey="completed_count" fill={successColor} name="Completed" />
          </BarChart>
        </ResponsiveContainer>
      )}
    </Paper>
  );

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={800}>
          Workflow Analytics
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 8 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Loading analytics...
          </Typography>
        </Box>
      ) : (
        <>
          {renderSummary()}
          {renderByTemplateChart()}
          {renderWeeklyTrendChart()}
          {renderBottleneckTable()}
          {renderStaffWorkloadChart()}
        </>
      )}
    </Box>
  );
};

export default WorkflowAnalytics;
